import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DEFAULT_GROUPING_CONFIG,
  DEFAULT_VIEW_TYPE,
  getDefaultDateRange,
  GroupingConfig,
  ReportFilters,
  ReportGroupingConfig,
  ReportViewType,
} from '../types/reports';

interface UseReportFiltersUrlReturn {
  initialFilters: Partial<ReportFilters>;
  updateUrl: (filters: ReportFilters) => void;
}

const parseGroupingFromUrl = (param: string | null): ReportGroupingConfig | undefined => {
  if (!param) return undefined;

  try {
    // Format: "project:1:true,user:0:false,issue:2:true"
    const levels: GroupingConfig[] = param.split(',').map((part) => {
      const [level, order, enabled] = part.split(':');
      return {
        level: level as 'project' | 'user' | 'issue' | 'label',
        order: parseInt(order, 10),
        enabled: enabled === 'true',
      };
    });

    if (levels.length >= 3 && levels.length <= 4) {
      // Backwards compatibility: add missing 'label' level for old URLs with 3 levels
      if (!levels.find((l) => l.level === 'label')) {
        const maxOrder = Math.max(...levels.map((l) => l.order));
        levels.push({ level: 'label', enabled: false, order: maxOrder + 1 });
      }
      return { levels };
    }
  } catch {
    // Invalid format, return undefined
  }
  return undefined;
};

const serializeGroupingToUrl = (config: ReportGroupingConfig): string => {
  return config.levels
    .map((l) => `${l.level}:${l.order}:${l.enabled}`)
    .join(',');
};

export const useReportFiltersUrl = (): UseReportFiltersUrlReturn => {
  const location = useLocation();
  const navigate = useNavigate();

  const initialFilters = useMemo((): Partial<ReportFilters> => {
    const params = new URLSearchParams(location.search);

    const filters: Partial<ReportFilters> = {};

    // Date range
    const startDate = params.get('from');
    const endDate = params.get('to');
    if (startDate && endDate) {
      filters.dateRange = { startDate, endDate };
    }

    // Selected users
    const users = params.get('users');
    if (users) {
      filters.selectedUsers = users.split(',').filter(Boolean);
    }

    // Selected projects
    const projects = params.get('projects');
    if (projects) {
      filters.selectedProjects = projects.split(',').filter(Boolean);
    }

    // Selected labels
    const labels = params.get('labels');
    if (labels) {
      filters.selectedLabels = labels.split(',').filter(Boolean);
    }

    // Grouping config
    const grouping = parseGroupingFromUrl(params.get('grouping'));
    if (grouping) {
      filters.groupingConfig = grouping;
    }

    // View type
    const view = params.get('view') as ReportViewType | null;
    if (view && ['daily', 'weekly', 'monthly'].includes(view)) {
      filters.viewType = view;
    }

    return filters;
  }, [location.search]);

  const updateUrl = useCallback(
    (filters: ReportFilters) => {
      const params = new URLSearchParams();
      const defaultDateRange = getDefaultDateRange();

      // Date range - only add if different from default
      if (
        filters.dateRange.startDate !== defaultDateRange.startDate ||
        filters.dateRange.endDate !== defaultDateRange.endDate
      ) {
        params.set('from', filters.dateRange.startDate);
        params.set('to', filters.dateRange.endDate);
      }

      // Selected users
      if (filters.selectedUsers && filters.selectedUsers.length > 0) {
        params.set('users', filters.selectedUsers.join(','));
      }

      // Selected projects
      if (filters.selectedProjects && filters.selectedProjects.length > 0) {
        params.set('projects', filters.selectedProjects.join(','));
      }

      // Selected labels
      if (filters.selectedLabels && filters.selectedLabels.length > 0) {
        params.set('labels', filters.selectedLabels.join(','));
      }

      // Grouping config - only add if different from default
      const defaultGrouping = serializeGroupingToUrl(DEFAULT_GROUPING_CONFIG);
      const currentGrouping = serializeGroupingToUrl(filters.groupingConfig);
      if (currentGrouping !== defaultGrouping) {
        params.set('grouping', currentGrouping);
      }

      // View type - only add if different from default
      if (filters.viewType && filters.viewType !== DEFAULT_VIEW_TYPE) {
        params.set('view', filters.viewType);
      }

      const search = params.toString();
      const newUrl = search ? `?${search}` : location.pathname;

      // Only update if URL actually changed
      if (location.search !== (search ? `?${search}` : '')) {
        navigate(newUrl, { replace: true });
      }
    },
    [location.pathname, location.search, navigate]
  );

  return { initialFilters, updateUrl };
};
